import { useEffect, useRef } from 'react';

/* ============================================================
   hvdm.io — hero wireframe background
   Perspective grid + edge-only polyhedra that rotate on their
   own and tilt toward the cursor. Monochrome, brand-aligned.
   No deps. Respects prefers-reduced-motion.
   ============================================================ */

type Vec = { x: number; y: number; z: number };
type Shape = { verts: number[][]; edges: [number, number][] };

const PHI = (1 + Math.sqrt(5)) / 2;

const rotX = (p: Vec, a: number): Vec => {
  const c = Math.cos(a),
    s = Math.sin(a);
  return { x: p.x, y: p.y * c - p.z * s, z: p.y * s + p.z * c };
};
const rotY = (p: Vec, a: number): Vec => {
  const c = Math.cos(a),
    s = Math.sin(a);
  return { x: p.x * c + p.z * s, y: p.y, z: -p.x * s + p.z * c };
};
const rotZ = (p: Vec, a: number): Vec => {
  const c = Math.cos(a),
    s = Math.sin(a);
  return { x: p.x * c - p.y * s, y: p.x * s + p.y * c, z: p.z };
};

const buildEdges = (verts: number[][], targetLenSq: number, tol: number): [number, number][] => {
  const edges: [number, number][] = [];
  for (let i = 0; i < verts.length; i++) {
    for (let j = i + 1; j < verts.length; j++) {
      const dx = verts[i][0] - verts[j][0];
      const dy = verts[i][1] - verts[j][1];
      const dz = verts[i][2] - verts[j][2];
      const d = dx * dx + dy * dy + dz * dz;
      if (Math.abs(d - targetLenSq) < tol) edges.push([i, j]);
    }
  }
  return edges;
};

const maxMag = (verts: number[][]) => {
  let max = 0;
  verts.forEach((v) => {
    const m = Math.hypot(v[0], v[1], v[2]);
    if (m > max) max = m;
  });
  return max;
};

const makeShape = (rawVerts: number[][], edgeLenSq: number, tol: number): Shape => {
  const max = maxMag(rawVerts);
  const verts = rawVerts.map((v) => [v[0] / max, v[1] / max, v[2] / max]);
  const f = Math.sqrt(edgeLenSq) / max;
  return { verts, edges: buildEdges(verts, f * f, tol) };
};

const ICO_RAW = [
  [0, 1, PHI],
  [0, -1, PHI],
  [0, 1, -PHI],
  [0, -1, -PHI],
  [1, PHI, 0],
  [-1, PHI, 0],
  [1, -PHI, 0],
  [-1, -PHI, 0],
  [PHI, 0, 1],
  [-PHI, 0, 1],
  [PHI, 0, -1],
  [-PHI, 0, -1]
];
const OCTA_RAW = [
  [1, 0, 0],
  [-1, 0, 0],
  [0, 1, 0],
  [0, -1, 0],
  [0, 0, 1],
  [0, 0, -1]
];
const CUBE_RAW = [
  [1, 1, 1],
  [1, 1, -1],
  [1, -1, 1],
  [1, -1, -1],
  [-1, 1, 1],
  [-1, 1, -1],
  [-1, -1, 1],
  [-1, -1, -1]
];

const ico = makeShape(ICO_RAW, 4, 0.06);
const octa = makeShape(OCTA_RAW, 2, 0.06);
const cube = makeShape(CUBE_RAW, 4, 0.06);

type SceneObject = {
  shape: Shape;
  cx: number;
  cy: number;
  scale: number;
  z: number;
  rx: number;
  ry: number;
  rz: number;
  baseRX: number;
  baseRY: number;
  weight: number;
};

const FOCAL = 3.0;

const HeroBackground = () => {
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const host = hostRef.current;
    if (!canvas || !host) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

    let W = 0,
      H = 0,
      DPR = 1;

    const resize = () => {
      DPR = Math.min(window.devicePixelRatio || 1, 2);
      const r = host.getBoundingClientRect();
      W = r.width;
      H = r.height;
      canvas.width = Math.round(W * DPR);
      canvas.height = Math.round(H * DPR);
      canvas.style.width = W + 'px';
      canvas.style.height = H + 'px';
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    };

    // positions are in a normalized space; x≈[-1.6..1.6], y≈[-1..1], z depth
    const objects: SceneObject[] = [
      {
        shape: ico,
        cx: 0.66,
        cy: -0.12,
        scale: 1.0,
        z: 0,
        rx: 0.16,
        ry: 0.22,
        rz: 0.0,
        baseRX: 0.5,
        baseRY: 0.3,
        weight: 1.0
      },
      {
        shape: octa,
        cx: 1.16,
        cy: 0.4,
        scale: 0.42,
        z: 0.6,
        rx: 0.3,
        ry: -0.18,
        rz: 0.0,
        baseRX: 0.1,
        baseRY: 0.8,
        weight: 0.55
      },
      {
        shape: cube,
        cx: 1.0,
        cy: -0.48,
        scale: 0.32,
        z: 0.9,
        rx: -0.22,
        ry: 0.26,
        rz: 0.12,
        baseRX: 0.7,
        baseRY: 0.4,
        weight: 0.4
      }
    ];

    const mouse = { tx: 0, ty: 0, x: 0, y: 0, active: 0, tActive: 0 };
    const onMove = (e: MouseEvent) => {
      const r = host.getBoundingClientRect();
      if (e.clientY < r.top || e.clientY > r.bottom) {
        mouse.tActive = 0;
        return;
      }
      mouse.tx = (e.clientX - r.left) / r.width - 0.5;
      mouse.ty = (e.clientY - r.top) / r.height - 0.5;
      mouse.tActive = 1;
    };
    const onOut = () => {
      mouse.tActive = 0;
    };

    const project = (p: Vec, originX: number, originY: number, unit: number, camZ: number) => {
      const z = p.z + camZ;
      const s = FOCAL / (FOCAL + z);
      return { x: originX + p.x * unit * s, y: originY + p.y * unit * s, s };
    };

    const drawGrid = (parX: number, parY: number) => {
      const horizonY = H * 0.46 + parY * 26;
      const vpX = W * 0.6 + parX * 60; // vanishing point
      const f = H * 0.62; // focal length (px)
      const camH = 1.0; // camera height above floor (world units)
      const Z_NEAR = 1,
        Z_FAR = 64; // evenly-spaced world depths
      const X_HALF = 20; // floor half-width in world units

      const sx = (x: number, z: number) => vpX + (x / z) * f;
      const sy = (z: number) => horizonY + (camH / z) * f;

      ctx.lineWidth = 1;

      // depth lines (constant Z) — fading into the horizon
      for (let z = Z_NEAR; z <= Z_FAR; z++) {
        const y = sy(z);
        if (y < horizonY) break;
        const f01 = (z - Z_NEAR) / (Z_FAR - Z_NEAR);
        const a = 0.15 * Math.pow(1 - f01, 1.7) * (1 - f01 * 0.25);
        if (a <= 0.004) continue;
        ctx.strokeStyle = `rgba(190,196,208,${a.toFixed(3)})`;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(W, y);
        ctx.stroke();
      }

      // rails (constant X) — radiate into the vanishing point
      for (let x = -X_HALF; x <= X_HALF; x++) {
        const x01 = Math.abs(x) / X_HALF;
        const a = 0.12 * (1 - x01 * 0.8) + 0.01;
        if (a <= 0.012) continue;
        ctx.strokeStyle = `rgba(190,196,208,${a.toFixed(3)})`;
        ctx.beginPath();
        ctx.moveTo(sx(x, Z_NEAR), sy(Z_NEAR));
        ctx.lineTo(sx(x, Z_FAR), sy(Z_FAR));
        ctx.stroke();
      }
    };

    const drawObject = (
      o: SceneObject,
      t: number,
      mx: number,
      my: number,
      parX: number,
      parY: number
    ) => {
      const unit = Math.min(W, H) * 0.3 * o.scale;
      const originX = W * 0.5 + (o.cx - 0.5) * Math.min(W * 0.92, 1180) + parX * 34 * o.weight;
      const originY = H * 0.46 + o.cy * Math.min(H * 0.7, 560) + parY * 30 * o.weight;

      const ax = t * o.rx + my * 0.9 * o.weight + o.baseRX;
      const ay = t * o.ry + mx * 1.3 * o.weight + o.baseRY;
      const az = t * o.rz;

      const pts = o.shape.verts.map((v) => {
        let p: Vec = { x: v[0], y: v[1], z: v[2] };
        p = rotX(p, ax);
        p = rotY(p, ay);
        if (az) p = rotZ(p, az);
        return project(p, originX, originY, unit, o.z);
      });

      for (const [a, b] of o.shape.edges) {
        const pa = pts[a],
          pb = pts[b];
        const depth = (pa.s + pb.s) / 2;
        const alpha = Math.min(0.6, Math.max(0.06, (depth - 0.5) * 0.9)) * (0.5 + o.weight * 0.5);
        ctx.strokeStyle = `rgba(225,228,235,${alpha.toFixed(3)})`;
        ctx.lineWidth = 0.6 + (depth - 0.5) * 1.4;
        ctx.beginPath();
        ctx.moveTo(pa.x, pa.y);
        ctx.lineTo(pb.x, pb.y);
        ctx.stroke();
      }
      for (const p of pts) {
        const r = 0.7 + (p.s - 0.5) * 2.2;
        if (r <= 0) continue;
        const a = Math.min(0.85, Math.max(0.12, (p.s - 0.55) * 1.4)) * (0.5 + o.weight * 0.5);
        ctx.fillStyle = `rgba(240,242,246,${a.toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    let raf = 0;
    let last = performance.now();
    let tt = 0;
    const frame = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (!reduce) tt += dt;

      mouse.x += (mouse.tx - mouse.x) * 0.06;
      mouse.y += (mouse.ty - mouse.y) * 0.06;
      mouse.active += (mouse.tActive - mouse.active) * 0.05;
      const mx = mouse.x * mouse.active;
      const my = mouse.y * mouse.active;

      ctx.clearRect(0, 0, W, H);
      drawGrid(mx, my);
      const order = objects.slice().sort((a, b) => b.z - a.z);
      for (const o of order) drawObject(o, tt, mx, my, mx, my);

      raf = requestAnimationFrame(frame);
    };

    resize();
    window.addEventListener('resize', resize, { passive: true });
    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('mouseout', onOut);
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseout', onOut);
    };
  }, []);

  return (
    <div
      ref={hostRef}
      aria-hidden
      className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
      <canvas ref={canvasRef} className="block h-full w-full" />
    </div>
  );
};

export default HeroBackground;
